// Copyright © 2019-2026 Focal Systems. All rights reserved.

/**
 * Named filter presets for a data-table page.
 *
 * A preset is nothing but a name plus the `encodeFilters` string a page already puts in its `?f=`
 * search param, so it needs no per-page schema: whatever keys a page's `FILTER_DEFAULTS` has are
 * what a preset carries.
 *
 * Two keys are deliberately excluded (see `PRESET_EXCLUDED_KEYS`):
 * - `page`, because applying a preset always lands on the first page — restoring a page index into
 *   a result set whose row count has since changed lands on an arbitrary or empty page.
 * - `pageSize`, because that's a per-viewer preference (profile → Appearance), not a property of
 *   the filter combination.
 */

import { encodeFilters } from "@/lib/filter-params";

export interface FilterPreset {
  /** User-supplied name. Also the identity — saving under an existing name replaces it. */
  name: string;
  /** `encodeFilters` output for the preset's filters, search and sort. Never carries page/pageSize. */
  value: string;
}

/** Longest accepted preset name; anything longer is truncated on save. */
export const PRESET_NAME_MAX_LENGTH = 40;

/** Filter keys a preset never captures. */
export const PRESET_EXCLUDED_KEYS = ["page", "pageSize"] as const;

/** Shares the `data-table:<storageKey>:` namespace with the column-visibility state. */
export function presetsStorageKey(storageKey: string): string {
  return `data-table:${storageKey}:presets`;
}

/**
 * The stored form of a page's current filter state: identical to what lands in `?f=`, minus the
 * excluded keys. Because `encodeFilters` omits anything equal to its default, forcing those keys
 * back to their defaults is enough to keep them out of the string entirely.
 */
export function encodePresetValue<T extends Record<string, unknown>>(current: T, defaults: T): string {
  const normalized: Record<string, unknown> = { ...current };
  for (const key of PRESET_EXCLUDED_KEYS) {
    if (key in defaults) normalized[key] = defaults[key];
  }
  return encodeFilters(normalized as T, defaults);
}

/** Trim, collapse runs of whitespace, and clamp to the length limit. */
export function normalizePresetName(name: string): string {
  return name.trim().replace(/\s+/g, " ").slice(0, PRESET_NAME_MAX_LENGTH);
}

/** Case-insensitive, so "UK stores" and "uk stores" are one preset rather than two look-alikes. */
function sameName(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

export function findPresetByName(presets: FilterPreset[], name: string): FilterPreset | undefined {
  const normalized = normalizePresetName(name);
  return presets.find((preset) => sameName(preset.name, normalized));
}

/** The preset matching a filter state exactly, used to show which one is currently applied. */
export function findPresetByValue(presets: FilterPreset[], value: string): FilterPreset | undefined {
  if (!value) return undefined;
  return presets.find((preset) => preset.value === value);
}

/**
 * Adds a preset, or replaces the one with the same name in place — keeping its position, so
 * re-saving a preset doesn't shuffle the menu under the cursor.
 */
export function upsertPreset(presets: FilterPreset[], name: string, value: string): FilterPreset[] {
  const preset: FilterPreset = { name: normalizePresetName(name), value };
  const index = presets.findIndex((existing) => sameName(existing.name, preset.name));
  if (index === -1) return [...presets, preset];
  return presets.map((existing, i) => (i === index ? preset : existing));
}

export function removePreset(presets: FilterPreset[], name: string): FilterPreset[] {
  return presets.filter((preset) => !sameName(preset.name, name));
}

/** Drops anything that isn't a `{name, value}` pair of strings, so hand-edited storage can't crash a page. */
function sanitize(parsed: unknown): FilterPreset[] {
  if (!Array.isArray(parsed)) return [];
  const presets: FilterPreset[] = [];
  for (const entry of parsed) {
    if (typeof entry !== "object" || entry === null) continue;
    const { name, value } = entry as Record<string, unknown>;
    if (typeof name !== "string" || typeof value !== "string") continue;
    const normalized = normalizePresetName(name);
    if (!normalized || !value) continue;
    if (presets.some((preset) => sameName(preset.name, normalized))) continue;
    presets.push({ name: normalized, value });
  }
  return presets;
}

export function loadPresets(storageKey: string): FilterPreset[] {
  if (typeof window === "undefined") return [];
  try {
    const stored = localStorage.getItem(presetsStorageKey(storageKey));
    return stored ? sanitize(JSON.parse(stored)) : [];
  } catch {
    // Malformed JSON, or localStorage unavailable (private mode, quota).
    return [];
  }
}

export function savePresets(storageKey: string, presets: FilterPreset[]): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(presetsStorageKey(storageKey), JSON.stringify(presets));
  } catch {
    // localStorage may be unavailable (private mode, quota) — presets stay in memory for the session.
  }
}
